import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ReportURLResponseModel } from "services/api/cplapi/CPLAPI";
import { InvoiceDistributionResponseModel } from "services/classes/InvoiceDistributionResponseModel";
import { ServiceChargeInvoiceAttachmentsModel } from "services/classes/ServiceChargeInvoiceAttachmentsModel";
import { IServiceChargeInvoicingService } from "services/ServiceChargeInvoicingService";
import { RootState } from "setup";
import { container } from "setup/dependency/inversify.config";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";

type InvoiceSendingProps = {
  EmailProgressResponse: InvoiceDistributionResponseModel;
  PrintProgressResponse: ReportURLResponseModel;
  IsPrinting: boolean;
  IsEmailing: boolean;
  IsSaving: boolean;
  IsAttaching: boolean;
};

const initialState: InvoiceSendingProps = {
  EmailProgressResponse: new InvoiceDistributionResponseModel(),
  PrintProgressResponse: new ReportURLResponseModel(),
  IsPrinting: false,
  IsEmailing: false,
  IsSaving: false,
  IsAttaching: false,
};

export const InvoiceSendingSlice = createSlice({
  name: "InvoiceSending",
  initialState,
  reducers: {
    setEmailProgressResponse: (state, action) => {
      state.EmailProgressResponse = action.payload;
    },
    setPrintProgressResponse: (state, action) => {
      state.PrintProgressResponse = action.payload;
    },
    setIsSavingInvoices: (state, action) => {
      state.IsSaving = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(printInvoices.pending, (state) => {
      state.IsPrinting = true;
    });
    builder.addCase(printInvoices.rejected, (state) => {
      state.IsPrinting = false;
    });
    builder.addCase(printInvoices.fulfilled, (state, action) => {
      state.PrintProgressResponse = action.payload;
      state.IsPrinting = false;
    });
    builder.addCase(emailInvoices.pending, (state) => {
      state.IsEmailing = true;
    });
    builder.addCase(emailInvoices.rejected, (state) => {
      state.IsEmailing = false;
    });
    builder.addCase(emailInvoices.fulfilled, (state) => {
      state.IsEmailing = false;
    });
    builder.addCase(addInvoiceAttachments.pending, (state) => {
      state.IsAttaching = true;
    });
    builder.addCase(addInvoiceAttachments.rejected, (state) => {
      state.IsAttaching = false;
    });
    builder.addCase(addInvoiceAttachments.fulfilled, (state) => {
      state.IsAttaching = false;
    });
  },
});

export const printInvoices = createAsyncThunk<ReportURLResponseModel, undefined, { state: RootState }>(
  "InvoiceSending/printInvoices",
  async (_, thunkAPI) => {
    const serviceChargeInvoicingService = container.get<IServiceChargeInvoicingService>(
      SERVICE_TYPES.ServiceChargeInvoicingService
    );
    const state = thunkAPI.getState();
    return await serviceChargeInvoicingService.printInvoices(state.serviceChargeInvoicing.ServiceChargeInvoice);
  }
);

export const emailInvoices = createAsyncThunk<void, undefined, { state: RootState }>(
  "InvoiceSending/emailInvoices",
  async (_, thunkAPI) => {
    const serviceChargeInvoicingService = container.get<IServiceChargeInvoicingService>(
      SERVICE_TYPES.ServiceChargeInvoicingService
    );
    const state = thunkAPI.getState();
    return await serviceChargeInvoicingService.emailInvoices(state.serviceChargeInvoicing.ServiceChargeInvoice);
  }
);

export const addInvoiceAttachments = createAsyncThunk<void, ServiceChargeInvoiceAttachmentsModel>(
  "InvoiceSending/addInvoiceAttachments",
  async (attachmentsModel) => {
    const serviceChargeInvoicingService = container.get<IServiceChargeInvoicingService>(
      SERVICE_TYPES.ServiceChargeInvoicingService
    );
    return await serviceChargeInvoicingService.addInvoiceAttachments(attachmentsModel);
  }
);

export const { setEmailProgressResponse, setPrintProgressResponse, setIsSavingInvoices } = InvoiceSendingSlice.actions;

export const selectEmailProgressResponse = (state: RootState) =>
  state.serviceChargeInvoiceSending.EmailProgressResponse;
export const selectPrintProgressResponse = (state: RootState) =>
  state.serviceChargeInvoiceSending.PrintProgressResponse;
export const selectIsPrinting = (state: RootState) => state.serviceChargeInvoiceSending.IsPrinting;
export const selectIsEmailing = (state: RootState) => state.serviceChargeInvoiceSending.IsEmailing;
export const selectIsSavingInvoices = (state: RootState) => state.serviceChargeInvoiceSending.IsSaving;

export default InvoiceSendingSlice.reducer;
